import React, { useState, useEffect } from 'react';
import { Box, IconButton, MenuItem, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

type FontSize = 'small' | 'medium' | 'large' | 'x-large';

interface AccessibilitySettings {
  fontSize: FontSize;
  highContrast: boolean;
  reducedMotion: boolean;
}

const STORAGE_KEY = 'dguide-accessibility-settings';

const fontSizeValues: Record<FontSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
  'x-large': '20px',
};

// "small" is the default (1×). Each step only makes things bigger.
const fontSizeMultipliers: Record<FontSize, number> = {
  small: 1,
  medium: 1.15,
  large: 1.3,
  'x-large': 1.45,
};

function applySettings(newSettings: AccessibilitySettings) {
  const root = document.documentElement;
  const body = document.body;

  const multiplier = fontSizeMultipliers[newSettings.fontSize];
  root.setAttribute('data-font-size', newSettings.fontSize);

  // Use CSS zoom on the .App container so everything scales uniformly —
  // text, icons, spacing — without touching individual components.
  const appEl = document.querySelector('.App') as HTMLElement | null;
  if (appEl) {
    (appEl.style as any).zoom = multiplier.toString();
  }

  if (newSettings.highContrast) {
    root.classList.add('high-contrast-mode');
    body.classList.add('high-contrast-mode');
    void body.offsetHeight;
  } else {
    root.classList.remove('high-contrast-mode');
    body.classList.remove('high-contrast-mode');
  }

  if (newSettings.reducedMotion) {
    root.classList.add('reduce-motion');
    body.classList.add('reduce-motion');
  } else {
    root.classList.remove('reduce-motion');
    body.classList.remove('reduce-motion');
  }

  window.dispatchEvent(new CustomEvent('accessibility-settings-changed', {
    detail: newSettings
  }));
}

export default function AccessibilityModalContent() {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n as any).language === 'he';
  const [settings, setSettings] = useState<AccessibilitySettings>({
    fontSize: 'small',
    highContrast: false,
    reducedMotion: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSettings(parsed);
        applySettings(parsed);
      } catch (e) {
        console.error('Failed to load accessibility settings', e);
      }
    }
  }, []);

  const updateSettings = (updates: Partial<AccessibilitySettings>) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
  };

  const handleReset = () => {
    const defaultSettings: AccessibilitySettings = {
      fontSize: 'small',
      highContrast: false,
      reducedMotion: false,
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <Box sx={{ p: 1, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {t('accessibility.accessibility_settings')}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('accessibility.font_size')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          {(['small', 'medium', 'large', 'x-large'] as FontSize[]).map((size) => {
            const px = parseInt(fontSizeValues[size]);
            const padding = Math.round(px * 0.6);
            return (
              <IconButton
                key={size}
                onClick={() => updateSettings({ fontSize: size })}
                aria-label={t(`accessibility.font_size_${size}`) as string}
                aria-pressed={settings.fontSize === size}
                sx={{
                  border: '2px solid',
                  borderColor: settings.fontSize === size ? 'primary.main' : 'grey.300',
                  bgcolor: settings.fontSize === size ? 'primary.light' : 'transparent',
                  fontSize: `${px}px`,
                  width: `${px + padding * 2}px`,
                  height: `${px + padding * 2}px`,
                  minWidth: 0,
                  padding: `${padding}px`,
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                A
              </IconButton>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <MenuItem
        onClick={() => updateSettings({ highContrast: !settings.highContrast })}
        aria-pressed={settings.highContrast}
        sx={{ justifyContent: 'space-between', py: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="fa-solid fa-circle-half-stroke" aria-hidden="true"></i>
          <Typography>{t('accessibility.high_contrast_mode')}</Typography>
        </Box>
        <Box sx={{
          width: 48, height: 24, borderRadius: 12,
          bgcolor: settings.highContrast ? 'primary.main' : 'grey.300',
          position: 'relative', transition: 'background-color 0.3s',
        }}>
          <Box sx={{
            position: 'absolute', top: 2,
            left: settings.highContrast ? 26 : 2,
            width: 20, height: 20, borderRadius: '50%',
            bgcolor: 'white', transition: 'left 0.3s',
          }} />
        </Box>
      </MenuItem>

      <MenuItem
        onClick={() => updateSettings({ reducedMotion: !settings.reducedMotion })}
        aria-pressed={settings.reducedMotion}
        sx={{ justifyContent: 'space-between', py: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="fa-solid fa-stopwatch" aria-hidden="true"></i>
          <Typography>{t('accessibility.reduce_motion')}</Typography>
        </Box>
        <Box sx={{
          width: 48, height: 24, borderRadius: 12,
          bgcolor: settings.reducedMotion ? 'primary.main' : 'grey.300',
          position: 'relative', transition: 'background-color 0.3s',
        }}>
          <Box sx={{
            position: 'absolute', top: 2,
            left: settings.reducedMotion ? 26 : 2,
            width: 20, height: 20, borderRadius: '50%',
            bgcolor: 'white', transition: 'left 0.3s',
          }} />
        </Box>
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <MenuItem
        onClick={handleReset}
        sx={{ justifyContent: 'center', color: 'error.main', py: 1.5 }}
      >
        <i className="fa-solid fa-rotate-left" aria-hidden="true" style={{ marginRight: 8 }}></i>
        {t('accessibility.reset_to_defaults')}
      </MenuItem>
    </Box>
  );
}
